import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { AutoSizer, Grid } from 'react-virtualized';
import { useMediaQuery } from 'react-responsive';
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';

import SlideThumbnailSortable, { SlideThumbnail } from '../SlideThumbnail';
import { isIntroLayout } from '../../../../../../helpers';
import { breakpointDown } from '../../../../../../../../../../../../services/responsive/responsive';
import Button from '../../../../../../../../../../../../components/global/Button';
import useOpenAlert from '../../../../../../../../../../../../hooks/useOpenAlert';

const propTypes = {
  slideshowSlides: PropTypes.arrayOf(
    PropTypes.shape({
      photo: PropTypes.shape({
        id: PropTypes.string,
      }),
    }),
  ).isRequired,
  currentSlideId: PropTypes.string,
  onThumbnailClick: PropTypes.func.isRequired,
  onMoveSlide: PropTypes.func.isRequired,
  onDeletePhoto: PropTypes.func.isRequired,
  onDeleteLayout: PropTypes.func.isRequired,
  loadingMore: PropTypes.bool,
  onLoadMore: PropTypes.func.isRequired,
  hasIntroSlide: PropTypes.bool,
  onReorderSlides: PropTypes.func.isRequired,
  className: PropTypes.string,
};
const SlidesSection = ({
  slideshowSlides,
  slidesTotal,
  slidesOffset,
  currentSlideId,
  onThumbnailClick,
  onMoveSlide,
  onDeletePhoto,
  onDeleteLayout,
  loadingMore,
  onLoadMore,
  hasIntroSlide,
  onReorderSlides,
  className,
}) => {
  const isMobile = useMediaQuery(breakpointDown('sm'));
  const [draggingSlide, setDraggingSlide] = useState(null);
  const [selectionEnabled, setSelectionEnabled] = useState(false);
  const [selectedSlideIds, setSelectedSlideIds] = useState([]);
  const openAlert = useOpenAlert();

  const getSlideId = slide =>
    slide.photo?.id ?? slide.asset_image?.id ?? slide.account_card?.id;

  const onResetSelection = () => {
    setSelectionEnabled(false);
    setSelectedSlideIds([]);
  };

  const onToggleSlideSelected = slideId => {
    setSelectedSlideIds(prev =>
      prev.includes(slideId)
        ? prev.filter(id => id !== slideId)
        : [...prev, slideId],
    );
  };

  const onSelectAll = () => {
    setSelectedSlideIds(
      slideshowSlides.map(slide => getSlideId(slide)).filter(Boolean),
    );
  };

  const onConfirmRemoveSelected = () => {
    const layoutEntries = [];
    const photoEntries = [];
    const introEntries = [];

    slideshowSlides.forEach((slide, idx) => {
      const id = getSlideId(slide);
      if (!selectedSlideIds.includes(id)) {
        return;
      }
      if (slide.account_card) {
        layoutEntries.push({ id: slide.account_card.id, idx });
      } else if (slide.asset_image) {
        introEntries.push({ id: slide.asset_image.id, idx });
      } else if (slide.photo) {
        photoEntries.push({ id: slide.photo.id, idx });
      }
    });

    if (layoutEntries.length > 0) {
      onDeleteLayout({
        account_card_ids: layoutEntries.map(({ id }) => id),
        slideIdxs: layoutEntries.map(({ idx }) => idx),
      });
    }
    if (photoEntries.length > 0) {
      onDeletePhoto({
        photoIds: photoEntries.map(({ id }) => id),
        photoIdxs: photoEntries.map(({ idx }) => idx),
      });
    }
    introEntries.forEach(({ id, idx }) => {
      onDeletePhoto({
        photoIds: [id],
        photoIdxs: [idx],
        isIntroSlide: true,
      });
    });

    onResetSelection();
  };

  const onRemoveSelected = () => {
    openAlert({
      type: 'info',
      dismissible: false,
      actionSize: 'sm',
      message: ({ onRequestClose: onAlertClose }) => (
        <div className="text-center">
          <h3 className="text-center">Remove selected slides?</h3>
          <p className="text-center text-light">
            Deleted slides cannot be restored. Are you sure you want to
            continue?
          </p>
          <Button
            btnStyle="danger"
            btnOutline
            btnBlock
            className="mb-2"
            onClick={() => {
              onConfirmRemoveSelected();
              onAlertClose();
            }}
          >
            Yes, remove selected items
          </Button>
          <Button
            btnStyle="link"
            className="text-extra-bold"
            onClick={onAlertClose}
          >
            Go back
          </Button>
        </div>
      ),
    });
  };

  // Drag and Drop Context Related

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      delay: 150,
      tolerance: 5,
    },
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 250,
      tolerance: 5,
    },
  });

  const onDragEnd = ({ active, over }) => {
    if (!active || !over) {
      return;
    }

    if (active.id !== over.id) {
      const oldIndex = slideshowSlides.findIndex(
        ({ photo, asset_image, account_card }) =>
          (photo && photo.id === active.id) ||
          (asset_image && asset_image.id === active.id) ||
          (account_card && account_card.id === active.id),
      );
      const newIndex = slideshowSlides.findIndex(
        ({ photo, asset_image, account_card }) =>
          (photo && photo.id === over.id) ||
          (asset_image && asset_image.id === over.id) ||
          (account_card && account_card.id === over.id),
      );
      const reorderedSlides = arrayMove(slideshowSlides, oldIndex, newIndex);
      onReorderSlides(reorderedSlides);
    }
  };

  const onDragStart = ({ active }) => {
    setDraggingSlide(active.data.current.slide);
  };

  // --

  const onSectionRendered = ({
    columnOverscanStopIndex,
    rowOverscanStopIndex,
  }) => {
    if (!onLoadMore || loadingMore) {
      return;
    }

    if (slideshowSlides.length === slidesTotal) {
      return;
    }

    if (
      isMobile &&
      (slidesOffset >= slideshowSlides.length ||
        columnOverscanStopIndex + 1 + slidesOffset >= slideshowSlides.length)
    ) {
      onLoadMore();
    }

    if (
      slidesOffset >= slideshowSlides.length ||
      rowOverscanStopIndex + 1 + slidesOffset >= slideshowSlides.length
    ) {
      onLoadMore();
    }
  };

  const cellRenderer = ({ columnIndex, rowIndex, key, style }) => {
    const slideIndex = isMobile ? columnIndex : rowIndex;
    const slideshowSlide = slideshowSlides[slideIndex];
    const isIntroSlide = isIntroLayout(slideshowSlide);
    const slideId = getSlideId(slideshowSlide);
    const isActive = currentSlideId && currentSlideId === slideId;
    const isSelected = selectedSlideIds.includes(slideId);

    return (
      <SlideThumbnailSortable
        key={key}
        style={style}
        slideshowSlide={slideshowSlide}
        isActive={isActive}
        selectable={selectionEnabled}
        isSelected={isSelected}
        onSelectToggle={() => onToggleSlideSelected(slideId)}
        onClick={() =>
          onThumbnailClick({
            slideId,
            slideIdx: isActive ? undefined : slideIndex,
          })
        }
        onDelete={() =>
          slideshowSlide.account_card
            ? onDeleteLayout({
                account_card_ids: [slideshowSlide.account_card.id],
                slideIdxs: [slideIndex],
              })
            : onDeletePhoto({
                photoIds: [
                  slideshowSlide.asset_image?.id ?? slideshowSlide.photo?.id,
                ],
                photoIdxs: [slideIndex],
                isIntroSlide: !!slideshowSlide.asset_image,
              })
        }
        onMoveUp={
          !onMoveSlide || isIntroSlide || slideIndex <= (hasIntroSlide ? 1 : 0)
            ? undefined
            : () => {
                onMoveSlide({ slideIdx: slideIndex, direction: -1 });
              }
        }
        onMoveDown={
          !onMoveSlide ||
          isIntroSlide ||
          slideIndex === slideshowSlides.length - 1
            ? undefined
            : () => {
                onMoveSlide({ slideIdx: slideIndex, direction: 1 });
              }
        }
      />
    );
  };

  const classes = classNames({
    'slides-section-wrapper': true,
    'no-intro-slide': !hasIntroSlide,
    [className]: !!className,
  });

  return (
    <DndContext
      onDragEnd={onDragEnd}
      onDragStart={onDragStart}
      sensors={[mouseSensor, touchSensor]}
    >
      <SortableContext
        items={slideshowSlides.map(
          ({ photo, asset_image, account_card }) =>
            photo?.id ?? asset_image?.id ?? account_card?.id,
        )}
        strategy={verticalListSortingStrategy}
      >
        <div className={classes}>
          <div
            className={classNames({
              'w-100': true,
              'd-md-none': slidesTotal > 0,
            })}
          >
            {slidesTotal > 0 && (
              <div className="d-flex align-items-center justify-content-between">
                <p className="text-uppercase text-extra-bold text-light text-xs mb-0">
                  Photo Slides
                </p>
                {!selectionEnabled ? (
                  <Button
                    btnStyle="link"
                    btnSize="xs"
                    className="text-extra-bold"
                    onClick={() => setSelectionEnabled(true)}
                  >
                    Select slides
                  </Button>
                ) : (
                  <div className="d-flex">
                    <Button
                      btnStyle="link"
                      btnSize="xs"
                      className="text-extra-bold"
                      onClick={onSelectAll}
                    >
                      Select all
                    </Button>
                    <Button
                      btnStyle="link"
                      btnSize="xs"
                      className="text-extra-bold text-danger ml-1"
                      onClick={onResetSelection}
                      disabled={selectedSlideIds.length === 0}
                    >
                      Deselect all
                    </Button>
                  </div>
                )}
              </div>
            )}
            {slidesTotal > 0 && !selectionEnabled && (
              <p className="mb-0 text-light text-italic text-xs ml-auto">
                Tap and drag slides to reorder
              </p>
            )}
          </div>
          <div
            className={classNames({
              'w-100': true,
              'h-100': !isMobile,
              'd-none': slidesTotal === 0,
            })}
          >
            <div className="slides-section-menu-container">
              <div className="w-100 d-none d-md-block mb-1">
                <div className="d-flex align-items-center justify-content-between">
                  <p className="text-uppercase text-extra-bold text-light text-xs mb-0">
                    Photo Slides
                  </p>
                  {slidesTotal > 0 && !selectionEnabled && (
                    <Button
                      btnStyle="link"
                      btnSize="xs"
                      className="text-extra-bold"
                      onClick={() => setSelectionEnabled(true)}
                    >
                      Select slides
                    </Button>
                  )}
                  {slidesTotal > 0 && selectionEnabled && (
                    <div className="d-flex">
                      <Button
                        btnStyle="link"
                        btnSize="xs"
                        className="text-extra-bold"
                        onClick={onSelectAll}
                      >
                        Select all
                      </Button>
                      <Button
                        btnStyle="link"
                        btnSize="xs"
                        className="text-extra-bold text-danger ml-1"
                        onClick={onResetSelection}
                        disabled={selectedSlideIds.length === 0}
                      >
                        Deselect all
                      </Button>
                    </div>
                  )}
                </div>
                {slidesTotal > 0 && !selectionEnabled && (
                  <p className="mb-0 text-light text-italic text-xs ml-auto">
                    {isMobile ? 'Tap' : 'Click'} and drag slides to reorder
                  </p>
                )}
              </div>
              <AutoSizer defaultHeight={450}>
                {({ width, height }) => {
                  return (
                    <Grid
                      autoContainerWidth={!isMobile}
                      width={width}
                      height={height}
                      columnWidth={220}
                      rowHeight={isMobile ? 120 : 160}
                      columnCount={isMobile ? slideshowSlides.length : 1}
                      rowCount={isMobile ? 1 : slideshowSlides.length}
                      cellRenderer={cellRenderer}
                      className="slides-section-menu"
                      containerStyle={{ overflow: 'visible' }}
                      onSectionRendered={onSectionRendered}
                    />
                  );
                }}
              </AutoSizer>
              <DragOverlay>
                {draggingSlide && (
                  <div className="secondary-photos-view-slides-step-slide-thumbnail-overlay">
                    <SlideThumbnail
                      onClick={() => {}}
                      slideshowSlide={draggingSlide}
                    />
                  </div>
                )}
              </DragOverlay>
            </div>
          </div>
          {selectionEnabled && (
            <div className="slides-section-selection-bar">
              <p className="mb-0 text-xs">{selectedSlideIds.length} selected</p>
              <div className="d-flex align-items-center">
                <Button
                  btnStyle="link"
                  btnSize="xs"
                  className="text-extra-bold text-danger"
                  onClick={onRemoveSelected}
                  disabled={selectedSlideIds.length === 0}
                >
                  Remove
                </Button>
                <Button
                  btnStyle="link"
                  btnSize="xs"
                  className="text-extra-bold ml-2"
                  onClick={onResetSelection}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </SortableContext>
    </DndContext>
  );
};

SlidesSection.propTypes = propTypes;

export default SlidesSection;
